// Fire-and-forget product view logger.
//
// Persists an anonymous browser session id (random, no PII) and writes one
// row per (handle, session) per ~30 minutes. Failures are swallowed so this
// never blocks render or affects UX.

import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { hasConsent } from "@/lib/consent";

const SESSION_KEY = "fl:vid:v1";
const SEEN_KEY = "fl:viewed:v1";
const DEDUP_MS = 30 * 60 * 1000; // 30 minutes

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = window.localStorage.getItem(SESSION_KEY);
    if (!id) {
      // 16 random bytes -> 32 hex chars (well within the 8..64 RLS check).
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);
      id = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
      window.localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

function shouldLog(handle: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.sessionStorage.getItem(SEEN_KEY);
    const seen: Record<string, number> = raw ? JSON.parse(raw) : {};
    const last = seen[handle];
    const now = Date.now();
    if (typeof last === "number" && now - last < DEDUP_MS) return false;
    seen[handle] = now;
    window.sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    return true;
  } catch {
    return true;
  }
}

export function useLogProductView(handle: string | undefined) {
  useEffect(() => {
    try {
      // The persistent session id is stored on the device, so it needs
      // statistics consent (see src/lib/consent.ts).
      if (!handle || !hasConsent("statistics")) return;
      if (!shouldLog(handle)) return;
      const session_id = getSessionId();
      if (!session_id) return;
      // Fire and forget — never await, never surface errors. The backend client
      // is lazy-created on first property access, so keep that access inside the
      // swallowed task as well; otherwise a missing env var can throw during the
      // first product-page effect and trip the route error boundary.
      void Promise.resolve()
        .then(() => supabase.from("product_views").insert({ handle, session_id }))
        .then(({ error }) => {
          if (error) console.debug("view-log failed", error.message);
        })
        .catch((error) => {
          console.debug("view-log failed", error instanceof Error ? error.message : error);
        });
    } catch (error) {
      console.debug("view-log failed", error instanceof Error ? error.message : error);
    }
  }, [handle]);
}
